import type { EnvironmentalSiteBreakdownItem } from '@siliconx/shared';
import { formatTonnes } from '../format';

/**
 * SIL-476 — "Scope 2 vs Scope 3 by site" dumbbell (per
 * `Environmental Fleet Detail.dc.html`). Position is scaled against the
 * MAX value across every site's own scope2/scope3 figures (not a fixed
 * 0-160 axis like the mock's hardcoded fixture) — the coverage caveat is
 * carried by the caller (`siteBreakdown.coverage`), never by the axis
 * itself.
 */
export function SiteDumbbell({ items }: { items: EnvironmentalSiteBreakdownItem[] }) {
  if (items.length === 0) {
    return (
      <div className="py-6 text-center text-xs text-zinc-400">No site-linked assets yet.</div>
    );
  }

  const maxVal = Math.max(...items.flatMap((r) => [r.scope2Kgco2e, r.scope3Kgco2e]), 1);
  const pct = (v: number) => `${Math.min(100, (v / maxVal) * 100).toFixed(1)}%`;

  return (
    <div className="flex flex-col gap-2.5">
      {items.map((r) => {
        const lo = Math.min(r.scope2Kgco2e, r.scope3Kgco2e);
        const hi = Math.max(r.scope2Kgco2e, r.scope3Kgco2e);
        return (
          <div key={r.dataCenterId} className="flex items-center gap-3 py-1 text-sm">
            <span className="w-32 shrink-0 truncate text-zinc-600" title={r.dataCenterName}>
              {r.dataCenterName}
            </span>
            <div className="relative h-4 flex-1">
              <div className="absolute inset-y-1/2 left-0 right-0 h-px -translate-y-1/2 bg-zinc-100" />
              <div
                className="absolute top-1/2 h-px -translate-y-1/2 bg-zinc-300"
                style={{ left: pct(lo), width: `calc(${pct(hi)} - ${pct(lo)})` }}
              />
              <span
                className="absolute top-1/2 size-3 -translate-y-1/2 -translate-x-1/2 rounded-full bg-indigo-500"
                style={{ left: pct(r.scope2Kgco2e) }}
                title={`Scope 2: ${formatTonnes(r.scope2Kgco2e)}`}
              />
              <span
                className="absolute top-1/2 size-3 -translate-y-1/2 -translate-x-1/2 rounded-full bg-emerald-500"
                style={{ left: pct(r.scope3Kgco2e) }}
                title={`Scope 3: ${formatTonnes(r.scope3Kgco2e)}`}
              />
            </div>
            <span className="w-32 shrink-0 text-right font-mono text-xs text-zinc-500">
              {formatTonnes(r.scope2Kgco2e)} / {formatTonnes(r.scope3Kgco2e)}
            </span>
          </div>
        );
      })}
      <p className="text-xs text-zinc-500">tCO2e · Scope 2 (indigo) vs Scope 3 (emerald), per linked site</p>
    </div>
  );
}
